import { Button, DynamicContainer, FormGroup, FormLabel, FormSection, Typeahead } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { FormikProps } from 'formik';
import { isNil, range } from 'ramda';
import React, { MutableRefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormCheckbox } from 'src/js/component/base/form/form-checkbox';
import { FormSelect, SelectOptions } from 'src/js/component/base/form/form-select';
import { FormTextfield } from 'src/js/component/base/form/form-textfield';
import systemInfo, { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { useStores } from 'src/js/hook/use-stores';
import { baudRateOptions } from 'src/js/pages/settings/comport/comport-view-model';
import { EssenceType } from 'src/js/pages/streaming/essence/essence-models';
import essenceModels from 'src/js/pages/streaming/essence/essence-store';
import videoEncoders from 'src/js/pages/streaming/video-encoder/video-encoder-store';
import videoInputs from 'src/js/pages/streaming/video-input/video-input-store';
import { FormChangeEvent } from 'src/js/util/global-type';
import { isNilOrEmpty } from 'src/js/util/global-util';
import { validateFormContext } from 'src/js/validator/validator';

import { MetadataCotRelay } from './metadata-cot-relay';
import {
  MetadataDecimationModeEnum,
  MetadataFormatEnum,
  MetadataTypeEnum,
  MetadataViewModel,
  metadataFormatToText,
} from './metadata-models';
import { MetadataSecurityInsertion } from './metadata-security-insertion';
import { changes } from 'src/js/util/formik-helper';

interface Props {
  applyButtonState: ButtonStateType;
  formikProps: FormikProps<MetadataViewModel>;
  formRef: MutableRefObject<FormikProps<MetadataViewModel>>;
  onCancel: VoidFunction;
}

export const MetadataEditForm = (props: Props) => {
  const { applyButtonState, formikProps, formRef, onCancel } = props;
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const values = formikProps.values;
  const newMetadata = values.id === -1;

  const typeOptions: SelectOptions = [
    { value: MetadataTypeEnum.NETWORK, label: t('metadata.settings.network') },
    {
      value: MetadataTypeEnum.INPUT,
      label: systemInfoCapabilities.is2110() ? t('metadata.settings.st2110') : t('metadata.settings.hdSDI'),
    },
  ];

  const [interfaceOptions, setInterfaceOptions] = useState<SelectOptions>([]);
  const getInterfaceOptions = () => {
    if (systemInfoCapabilities.is2110()) {
      const st2110VideoOptions = range(0, systemInfo.videoInputs).map((input) => ({
        value: input,
        label: (input + 1).toString(),
        disabled: true,
      }));

      essenceModels
        .filter((e) => e.type === EssenceType.data)
        .map((input) => {
          const option = st2110VideoOptions.find((option: any) => option.value == input.inputChannel);
          if (option) {
            option.label = !isNilOrEmpty(input.name) ? `${option.label} (${input.name})` : option.label;
            option.disabled = false;
          }
        });
      setInterfaceOptions(st2110VideoOptions);
    } else {
      setInterfaceOptions(
        videoInputs.map((input) => ({
          value: input.id,
          label: input.name,
        })),
      );
    }
  };

  const interfaceDisabled = (): boolean => {
    return formikProps?.values.id === formikProps?.values.interface + 1 || interfaceOptions.length === 1;
  };

  const decimationModeOptions: SelectOptions = [
    {
      value: MetadataDecimationModeEnum.NONE,
      label: t('metadata.settings.decimationMode.none'),
    },
    {
      value: MetadataDecimationModeEnum.BYFACTOR,
      label: t('metadata.settings.decimationMode.byFactor'),
    },
    {
      value: MetadataDecimationModeEnum.WITHENCODER,
      label: t('metadata.settings.decimationMode.withEncoder'),
    },
  ];

  const [decimationLabel, setDecimationLabel] = useState<string>();
  const getDecimationLabel = (decimationMode: MetadataDecimationModeEnum) => {
    setDecimationLabel(
      decimationMode === MetadataDecimationModeEnum.BYFACTOR
        ? t('metadata.settings.decimationMode.factor')
        : decimationMode === MetadataDecimationModeEnum.WITHENCODER
        ? t('metadata.settings.decimationMode.encoder')
        : '',
    );
  };

  const [decimationOptions, setDecimationOptions] = useState<SelectOptions>();
  const getDecimationOptions = (decimationMode: MetadataDecimationModeEnum) => {
    switch (decimationMode) {
      case MetadataDecimationModeEnum.BYFACTOR:
        setDecimationOptions(
          range(2, 61).map((v) => ({
            value: v,
            label: `1/${v}`,
          })),
        );
        break;

      case MetadataDecimationModeEnum.WITHENCODER:
        setDecimationOptions(
          videoEncoders.map((v) => ({
            value: v.id,
            label: v.name,
            disabled: v.interface !== formikProps.values.interface,
          })),
        );
        break;

      case MetadataDecimationModeEnum.NONE:
      default:
        setDecimationOptions([]);
    }
  };

  // to be run when changing SDI input
  const fixDecimateWithEncoder = (
    interfaceNumber: number,
    decimationMode: MetadataDecimationModeEnum,
    currentEncoderOption: number,
    type: MetadataTypeEnum,
  ) => {
    if (type === MetadataTypeEnum.INPUT && decimationMode === MetadataDecimationModeEnum.WITHENCODER) {
      let restoreValue = false;
      let firstValue: number = null;

      videoEncoders.forEach((enc) => {
        if (enc.interface === interfaceNumber) {
          if (firstValue === null) {
            firstValue = enc.id;
          }
        } else {
          // this one is disabled because we dont have the same interface
          if (enc.id === currentEncoderOption) {
            restoreValue = true; // we cannot use a disabled option
          }
        }
      });

      if (restoreValue && firstValue !== null && !isNil(formikProps)) {
        formikProps.setFieldValue('decimation', firstValue, true);
        formikProps.setFieldTouched('decimation');
      }
    }
  };

  const onChangeDecimationMode = (formEvent: FormChangeEvent) => {
    const { value } = formEvent.target;
    const decimation =
      value === MetadataDecimationModeEnum.BYFACTOR
        ? formikProps.values.decimationMode === MetadataDecimationModeEnum.BYFACTOR
          ? formikProps.values.decimation
          : 2
        : formikProps.values.decimationMode === MetadataDecimationModeEnum.WITHENCODER
        ? formikProps.values.decimation
        : 1;

    const decimationMode = value as MetadataDecimationModeEnum;
    formikProps.setFieldValue('decimationMode', decimationMode, true);
    formikProps.setFieldTouched('decimationMode');
    getDecimationLabel(decimationMode);
    getDecimationOptions(decimationMode);

    formikProps.setFieldValue('decimation', decimation, true);
    formikProps.setFieldTouched('decimation');

    fixDecimateWithEncoder(formikProps.values.interface, decimationMode, decimation, formikProps.values.type);
  };

  useEffect(() => {
    getInterfaceOptions();
    getDecimationLabel(values.decimationMode);
    getDecimationOptions(values.decimationMode);
    fixDecimateWithEncoder(values.interface, values.decimationMode, values.decimation, values.type);
  }, [formikProps.values]); // If we do [formikProps] it will fire twice per form change
  // If we do [formikProps.values] it will fire once per form change

  const onChangeInterface = (formEvent: FormChangeEvent) => {
    const { value } = formEvent.target;
    fixDecimateWithEncoder(
      Number(value),
      formikProps.values.decimationMode,
      formikProps.values.decimation,
      formikProps.values.type,
    );
  };

  const handleSelectSpiUid = (spiUid: string | undefined) => {
    formikProps.setFieldValue('spiUid', spiUid || '', true);
  };

  const handleChangeSpiUid = (event: FormChangeEvent) => {
    formikProps.setFieldValue('spiUid', event.target.value, true);
  };

  const dataFormatOptions: SelectOptions = [];
  if (systemInfoCapabilities.hasKLV()) {
    dataFormatOptions.push({
      value: MetadataFormatEnum.KLV,
      label: metadataFormatToText(MetadataFormatEnum.KLV),
    });
  }
  if (systemInfoCapabilities.hasCoT()) {
    dataFormatOptions.push({
      value: MetadataFormatEnum.COT,
      label: metadataFormatToText(MetadataFormatEnum.COT),
    });
  }

  const standardOptions = [
    { value: 0, label: 'RS-232' },
    { value: 1, label: 'RS-422' },
  ];

  let applyButtonEnabled = formikProps.dirty;
  const changed = changes(values, formikProps.initialValues);
  // console.log('changes', formikProps.dirty, changed)
  if (values.decimationMode === MetadataDecimationModeEnum.NONE) {
    delete changed.decimation;
  }
  delete changed.stopBits; // it's read-only
  if (Object.keys(changed).length === 0) {
    applyButtonEnabled = false;
  }

  return (
    <>
      <DynamicContainer minColumns={2} maxColumns={2} className="mk-mt-panel-name-align">
        <FormTextfield name="name" mask title={t('general.name')} className="max-width" autoFocus={newMetadata} />
        {!newMetadata && !sessionStore.isUser() && (
          <div className="d-flex ml-auto hai-mt-panel-apply-sticky">
            <Button className="hai-mr-6" onClick={onCancel}>
              {t('general.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => validateFormContext(formikProps, t)}
              disabled={!applyButtonEnabled}
              state={applyButtonState}
            >
              {t('general.apply')}
            </Button>
          </div>
        )}
      </DynamicContainer>
      {/* SOURCE SETTINGS */}
      {(newMetadata || (values.type === MetadataTypeEnum.INPUT && interfaceOptions.length > 0)) && (
        <FormSection className="hai-mb-6" title={t('metadata.settings.source')}>
          <DynamicContainer minColumns={3}>
            {newMetadata && (
              <FormSelect name="type" title={t('metadata.settings.metadataType')} options={typeOptions} />
            )}
            {values.type === MetadataTypeEnum.INPUT && interfaceOptions.length > 0 && (
              <FormSelect
                name="interface"
                disabled={interfaceDisabled()}
                title={
                  systemInfoCapabilities.is2110() ? t('metadata.settings.input') : t('metadata.settings.hdSDIInput')
                }
                options={interfaceOptions}
                onChange={onChangeInterface}
              />
            )}
          </DynamicContainer>
        </FormSection>
      )}
      {/* DECIMATION SETTINGS */}
      {values.type === MetadataTypeEnum.INPUT && (
        <FormSection className="hai-mb-6" title={t('metadata.settings.decimation')}>
          <DynamicContainer minColumns={3}>
            <FormSelect
              name="decimationMode"
              title={t('metadata.settings.decimation')}
              options={decimationModeOptions}
              onChange={onChangeDecimationMode}
            />
            {values.decimationMode !== MetadataDecimationModeEnum.NONE && decimationOptions && (
              <FormSelect name="decimation" title={decimationLabel} options={decimationOptions} />
            )}
          </DynamicContainer>
        </FormSection>
      )}
      {/* INPUT SETTINGS */}
      {values.type !== MetadataTypeEnum.INPUT && (
        <FormSection className="hai-mb-6" title={t('metadata.settings.input')}>
          <DynamicContainer minColumns={3}>
            <FormSelect name="format" title={t('metadata.settings.dataFormat')} options={dataFormatOptions} />
            {values.format === MetadataFormatEnum.COT && (
              <>
                <FormTextfield
                  name="delta"
                  title={t('metadata.settings.delta')}
                  hint={t('metadata.settings.deltaExplain')}
                  type="number"
                />
                <FormCheckbox
                  name="sensorDiscovery"
                  label={t('metadata.settings.sensorDiscovery')}
                  checked={!!values.sensorDiscovery}
                />
                <FormGroup>
                  <FormLabel>{t('metadata.settings.spiUid')}</FormLabel>
                  <Typeahead
                    name="spiUid"
                    noMatches={t('general.noMatches')}
                    initialValue={
                      values?.stats?.sensorUIDs?.length > 0
                        ? values.stats.sensorUIDs.find((s) => s === values.spiUid)
                        : values.spiUid
                    }
                    listItems={
                      values?.stats?.sensorUIDs
                        ? values.stats.sensorUIDs.map((s) => ({
                            eventKey: s,
                            title: s,
                          }))
                        : []
                    }
                    onChange={handleChangeSpiUid}
                    onSelect={handleSelectSpiUid}
                  />
                </FormGroup>
              </>
            )}
          </DynamicContainer>
        </FormSection>
      )}
      {/* NETWORK SETTING */}
      {values.type === MetadataTypeEnum.NETWORK && (
        <FormSection className="hai-mb-6" title={t('metadata.settings.network')}>
          <DynamicContainer minColumns={3}>
            <FormTextfield name="address" mask title={t('metadata.settings.address')} placeholder={t('general.any')} />
            <FormTextfield name="port" mask title={t('metadata.settings.port')} type="number" required />
          </DynamicContainer>
        </FormSection>
      )}
      {/* SERIAL PORT SETTING*/}
      {values.type === MetadataTypeEnum.SERIAL && (
        <FormSection className="hai-mb-6" title={t('settings.comport.title')}>
          <DynamicContainer minColumns={3}>
            <FormSelect name="standard" title={t('settings.comport.form.standard')} options={standardOptions} />
            <FormSelect name="baudRate" title={t('settings.comport.form.baudRate')} options={baudRateOptions} />
            <FormSelect
              name="dataBits"
              title={t('settings.comport.form.dataBits')}
              options={[{ value: 8, label: '8' }]}
            />
            <FormSelect
              name="parity"
              title={t('settings.comport.form.parity')}
              options={[{ value: 0, label: 'None' }]}
            />
            <FormSelect
              name="stopBits"
              title={t('settings.comport.form.stopBits')}
              options={[{ value: 1, label: '1' }]}
            />
            <FormSelect
              name="flowControl"
              title={t('settings.comport.form.flowControl')}
              options={[{ value: 0, label: 'None' }]}
            />
          </DynamicContainer>
        </FormSection>
      )}
      {/* KLV SETTINGS */}
      {values.format === MetadataFormatEnum.KLV && systemInfoCapabilities.hasKLV() && (
        <MetadataSecurityInsertion values={values} formRef={formRef} />
      )}
      {values.format === MetadataFormatEnum.COT && (
        <>
          {systemInfoCapabilities.hasCoT() && <MetadataCotRelay values={values} formRef={formRef} />}
          {systemInfoCapabilities.hasKLV() && <MetadataSecurityInsertion values={values} formRef={formRef} />}
        </>
      )}
    </>
  );
};
